import React from 'react';
import {
  Modal as ChakraModal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  ModalProps as ChakraModalProps,
  ModalHeader,
  HStack,
  Box,
  HTMLChakraProps,
  ModalContentProps,
  ModalBodyProps,
} from '@chakra-ui/react';
import { IconButton, Button, ButtonProps } from 'Atoms';
import { Typography } from 'Tokens';
import { RemoveIcon } from 'Tokens/Icons/Function';
import { HelpIcon } from 'Tokens/Icons/Status';
import { useTranslation } from 'utils/translation';

type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export const ModalTheme = {
  baseStyle: {
    backgroundColor: 'bg.elevated.overlay',
    header: {
      width: '100%',
      justifyContent: 'space-between',
      paddingX: '16px',
      paddingTop: '16px',
      backgroundColor: 'bg.elevated.overlay',
      paddingBottom: '0px',
    },
    footer: {
      padding: '16px',
      backgroundColor: 'bg.elevated.overlay',
      justifyContent: 'space-between',
    },
    body: {
      padding: '16px',
      backgroundColor: 'bg.elevated.overlay',
    },
    overlay: {
      backgroundColor: 'bg.backdrop',
    },
  },
  sizes: {
    xs: {
      dialog: {
        maxW: '288px',
      },
    },
    sm: {
      dialog: {
        maxW: '488px',
      },
    },
    md: {
      dialog: {
        maxW: '624px',
      },
    },
    lg: {
      dialog: {
        maxW: '840px',
      },
    },
    xl: {
      dialog: {
        maxW: '1040px',
      },
    },
    xxl: {
      dialog: {
        h: '95vh',
        w: '95vw',
        maxW: '1720px',
        overflow: 'auto',
      },
      dialogContainer: {
        overflow: 'unset',
        alignItems: 'center',
      },
    },
  },
};

export type ModalProps = {
  title?: string;
  titleIcon?: JSX.Element;
  customHeader?: JSX.Element;
  hasFooter?: boolean;
  hasCancelButton?: boolean;
  hasOverlay?: boolean;
  size?: ModalSize;
  helpIcon?: boolean;
  helpIconTooltip?: string;
  onHelpClick?: () => void;
  iconChoice?: JSX.Element;
  iconButtonProps?: ButtonProps;
  subtitle?: string;
  overline?: string;
  tag?: JSX.Element;
  confirmVariant?: any;
  confirmText?: string;
  onConfirm?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  confirmButtonProps?: ButtonProps;
  cancelVariant?: any;
  cancelText?: string;
  onCancel?: () => void;
  cancelButtonProps?: ButtonProps;
  actions?: React.ReactNode[];
  footerBorder?: boolean;
  hasHeader?: boolean;
  topRightIcon?: JSX.Element;
  illustration?: JSX.Element;
  loading?: boolean;
  containerProps?: HTMLChakraProps<'div'>;
  contentProps?: ModalContentProps;
  bodyProps?: ModalBodyProps;
  isCentered?: boolean;
  bgColor?: string;
  headerBgColor?: string;
  marginY?: string;
} & Pick<ChakraModalProps, 'onClose' | 'children' | 'isOpen' | 'trapFocus'>;

const EMPTY = () => {};

export function Modal({
  title,
  titleIcon,
  customHeader,
  isOpen,
  onClose,
  children,
  hasFooter = true,
  hasCancelButton = true,
  hasOverlay = true,
  hasHeader = true,
  subtitle,
  overline,
  helpIcon,
  helpIconTooltip,
  onHelpClick,
  iconChoice = <HelpIcon color="inherit" />,
  iconButtonProps,
  tag,
  confirmVariant = 'primary',
  confirmText = 'Confirm',
  onConfirm,
  cancelVariant = 'ghost',
  cancelText = 'Cancel',
  onCancel,
  confirmButtonProps,
  cancelButtonProps,
  actions,
  size = 'sm',
  trapFocus = true,
  footerBorder = false,
  topRightIcon,
  illustration,
  loading,
  containerProps,
  contentProps = {},
  bodyProps = {},
  isCentered = false,
  bgColor = 'bg.elevated.overlay',
  headerBgColor,
  marginY,
  ...props
}: ModalProps) {
  const isClosable = !!onClose;
  const { t } = useTranslation('common');
  return (
    <ChakraModal
      onClose={onClose ?? EMPTY}
      isOpen={isOpen}
      closeOnEsc={isClosable}
      closeOnOverlayClick={isClosable}
      size={size}
      trapFocus={trapFocus}
      isCentered={isCentered}
      returnFocusOnClose={false}
      {...props}
    >
      {hasOverlay && <ModalOverlay />}

      <ModalContent
        containerProps={containerProps}
        bgColor={bgColor}
        {...contentProps}
        marginY={marginY}
      >
        {/* Header */}
        {hasHeader && (
          <ModalHeader as={HStack} borderRadius={size} alignItems="start" bgColor={headerBgColor}>
            {customHeader ? (
              customHeader
            ) : (
              <Box width="90%">
                {/* Illustration */}
                {illustration && <Box marginBottom="24px">{illustration}</Box>}
                {/* Overline */}
                {overline && (
                  <Typography variant="detail" noOfLines={1} marginBottom="2px">
                    {overline}
                  </Typography>
                )}
                <HStack alignItems="center" spacing="6px">
                  {titleIcon}
                  <Typography variant="h2">
                    <span style={{ marginRight: '8px' }}>{title}</span>
                    {/* Icon */}
                    {helpIcon && (
                      <IconButton
                        size="xs"
                        variant="ghost"
                        icon={iconChoice}
                        onClick={onHelpClick}
                        aria-label="help icon"
                        {...iconButtonProps}
                        marginTop="-4px"
                        tooltipLabel={helpIconTooltip ?? t('common:actions.help')}
                        tooltipHidden={!helpIconTooltip}
                      />
                    )}
                    {/* Tag */}
                    {tag && tag}
                  </Typography>
                </HStack>

                {/* Subtitle */}
                {subtitle && (
                  <Typography variant="detail" noOfLines={1} marginTop="2px">
                    {subtitle}
                  </Typography>
                )}
              </Box>
            )}

            <HStack alignItems="start">
              {topRightIcon}
              {isClosable && (
                <IconButton
                  size="md"
                  variant="ghost"
                  onClick={onClose}
                  aria-label="close modal"
                  icon={<RemoveIcon color="inherit" />}
                  alignSelf="start"
                />
              )}
            </HStack>
          </ModalHeader>
        )}

        {/* Body */}
        <ModalBody
          paddingBottom={hasFooter ? '16px' : '32px !important'}
          borderRadius={!hasFooter || !hasHeader ? size : 'unset'}
          {...bodyProps}
        >
          {children}
        </ModalBody>

        {/* Footer */}
        {hasFooter && (
          <ModalFooter
            borderTop={footerBorder ? '1px solid' : ''}
            borderColor="border.decorative"
            borderRadius={size}
          >
            <HStack spacing="8px">{actions?.map((action) => action)}</HStack>
            <HStack spacing="8px">
              {hasCancelButton && (
                <Button
                  variant={cancelVariant}
                  onClick={onCancel ?? onClose}
                  {...cancelButtonProps}
                >
                  {cancelText}
                </Button>
              )}
              <Button
                variant={confirmVariant}
                onClick={loading ? (e) => e.preventDefault() : onConfirm}
                isLoading={loading}
                {...confirmButtonProps}
              >
                {confirmText}
              </Button>
            </HStack>
          </ModalFooter>
        )}
      </ModalContent>
    </ChakraModal>
  );
}
